import { ThemeProvider } from '@emotion/react';
import { percentageFormatter } from '@dnhb/formatters';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'shared/hooks';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ToolLoader } from 'shared/components/Loader';
import defaultTheme from 'shared/defaultTheme';
import { INIT } from 'shared/wizard/actions';
import ToolContainer from 'shared/wizard/ToolContainer';
import { getSettings } from './helpers';
import { lazyWithRetry, useTrackPageview } from "shared/utility";

const applicantFormPromise = import(
  /* webpackChunkName: "applicant-form" */
  'shared/forms/ApplicantForm'
);
const currentMortgageFormPromise = import(
  /* webpackChunkName: "current-mortgage-form" */
  'shared/forms/CurrentMortgageForm'
);
const expensesFormPromise = import(
  /* webpackChunkName: "expenses-form" */
  'shared/forms/ExpensesForm'
);
const leadInformationPromise = import(
    /* webpackChunkName: "lead-information-form" */
    'shared/forms/LeadInformationForm'
    );
const partnerFormPromise = import(
  /* webpackChunkName: "partner-form" */
  'shared/forms/PartnerForm'
);
const outputFinalPromise = import(
  /* webpackChunkName: "kidwk-output-final" */
  './components/OutputFinal'
);
const wizardFinalPromise = import(
  /* webpackChunkName: "kidwk-wizard-final" */
  './components/WizardFinal'
);
const wizardLandingPromise = import(
  /* webpackChunkName: "kidwk-wizard-landing" */
  './components/WizardLanding'
);

const WizardLanding = lazyWithRetry(() => wizardLandingPromise);
const CurrentMortgageForm = lazyWithRetry(() => currentMortgageFormPromise);
const ExpensesForm = lazyWithRetry(() => expensesFormPromise);
const LeadInformationForm = lazyWithRetry(() => leadInformationPromise);
const PartnerForm = lazyWithRetry(() => partnerFormPromise);
const OutputFinal = lazyWithRetry(() => outputFinalPromise);
const WizardFinal = lazyWithRetry(() => wizardFinalPromise);
const ApplicantForm = lazyWithRetry(() => applicantFormPromise);

const App = () => {
  const { app, settings = null, meta, framed, pages } = useSelector(state => state);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState(defaultTheme);
  const base = `${app}${framed ? '/framed' : ''}`;

  useEffect(() => {
    navigate(`/${app}${framed ? '/framed' : ''}/${pages[0] + location.search}`, {
      state: { page: pages[0] },
      replace: true,
    });
  }, [framed]);

  useEffect(() => {
    getSettings((data) => {
      dispatch({
        type: INIT,
        payload: data,
      });
      setTheme(theme => ({ ...theme, ...data.settings }));
    });
  }, []);

  useTrackPageview();

  return !settings ? (
    <ToolLoader />
  ) : (
    <ThemeProvider theme={theme}>
      <ToolContainer
        Input={
          <Routes>
            <Route path={`${base}/applicant`} element={<ApplicantForm />} />
            <Route path={`${base}/partner`} element={<PartnerForm />} />
            {settings?.askCurrentMortgage && (
              <Route path={`${base}/current-mortgage`} element={<CurrentMortgageForm />} />
            )}
            <Route path={`/${base}/applicant-information`} element={<LeadInformationForm />} />
            <Route path={`${base}/expenses`} element={<ExpensesForm />} />
            <Route path={`${base}/final`} element={<WizardFinal />} />
            <Route path={`${base}/landing`} element={<WizardLanding />} />
          </Routes>
        }
        Output={
          <Routes>
            <Route path={`${base}/final`} element={<OutputFinal />} />
          </Routes>
        }
        customDisclaimer={
          <>
            <p>
              De bankgarantie wordt berekend op basis van {percentageFormatter(meta.nhg_cost_percentage)} van de
              koopsom.
            </p>
            <p>
              Is er sprake van een studieschuld dan nemen wij in de berekening{' '}
              {percentageFormatter(meta.student_loan_weight)} van de schuld mee als maandlast.
            </p>
            <p>
              We vermelden deze niet apart, maar deze berekening van je maximale hypotheek houdt rekening met de
              risicoklasse waarin deze zal vallen.
            </p>
            <p>
              Het getoonde rentepercentage is het meest gunstige beschikbare rentepercentage waarmee je de woning kunt
              kopen. Wij gaan daarbij uit van een looptijd van 30 jaar en 10 jaar rentevast.
            </p>
            <p>
              Bij de bereking kijken we naar de actuele rentepercentages en niet naar de voorwaarden die de
              geldverstrekkers daarbij hanteren.
            </p>
            <p>Je adviseur kan een exacte berekening voor je maken.</p>
          </>
        }
      />
    </ThemeProvider>
  );
};

export default App;
